import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Search, 
  RefreshCw, 
  ExternalLink, 
  TrendingUp, 
  MessageSquare, 
  ThumbsUp,
  Clock,
  Plus,
  Eye,
  Settings
} from 'lucide-react';

interface SocialResult {
  id: string;
  platform: string;
  content_text: string;
  content_url: string;
  author_name?: string;
  author_username?: string;
  published_at: string;
  engagement_metrics: any;
  sentiment_score?: number;
  found_at: string;
}

interface ListeningQuery {
  id: string;
  name: string;
  keywords: string[];
  platforms: string[];
  is_active: boolean;
  last_scanned_at?: string;
}

const RealSocialFeed = () => {
  const { user } = useAuth();
  const [results, setResults] = useState<SocialResult[]>([]);
  const [queries, setQueries] = useState<ListeningQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] = useState<string[]>([]);
  const [showAddQuery, setShowAddQuery] = useState(false);
  const [newQuery, setNewQuery] = useState('');

  // Load connected platforms on mount
  useEffect(() => {
    loadConnectedPlatforms();
  }, [user]);

  const loadConnectedPlatforms = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('social_accounts')
        .select('platform')
        .eq('user_id', user.id)
        .eq('is_active', true);
      
      if (error) throw error;
      
      const platforms = ['reddit', 'hackernews', 'news']; // Always include these public sources
      if (data) {
        data.forEach(account => {
          if (account.platform === 'twitter' && !platforms.includes('twitter')) {
            platforms.push('twitter');
          }
        });
      }
      
      setConnectedPlatforms(platforms);
    } catch (error) {
      console.error('Error loading connected platforms:', error);
      setConnectedPlatforms(['reddit', 'hackernews', 'news']); // fallback
    }
  };

  const loadTwitterFeeds = async () => {
    if (!user) return;
    
    try {
      console.log('🐦 Loading Twitter feeds for connected accounts...');
      const { data, error } = await supabase.functions.invoke('fetch-twitter-feed', {
        body: { user_id: user.id }
      });
      
      console.log('📡 Twitter feed response:', { data, error });
      
      if (error) {
        console.error('❌ Error loading Twitter feeds:', error);
        
        // Show specific error messages to the user
        if (error.message?.includes('authentication failed') || error.message?.includes('invalid')) {
          toast.error('Twitter authentication failed. Please reconnect your Twitter account.', {
            duration: 8000
          });
        } else {
          toast.error(`Failed to fetch Twitter feed: ${error.message || 'Unknown error'}`, {
            duration: 6000
          });
        }
        return;
      }
      
      if (data && data.tweets) {
        console.log('✅ Twitter feeds loaded:', data.tweets.length, 'tweets');
        
        // Convert Twitter data to social listening results format
        const twitterResults = data.tweets.map((tweet: any) => ({
          id: `twitter-${tweet.id}`,
          platform: 'twitter',
          content_text: tweet.text,
          content_url: `https://twitter.com/i/status/${tweet.id}`,
          author_name: tweet.author?.name || 'Twitter User',
          author_username: tweet.author?.username || 'unknown',
          published_at: tweet.created_at,
          engagement_metrics: {
            likes: tweet.public_metrics?.like_count || 0,
            retweets: tweet.public_metrics?.retweet_count || 0,
            replies: tweet.public_metrics?.reply_count || 0,
            quotes: tweet.public_metrics?.quote_count || 0
          },
          sentiment_score: 0.5, // neutral default
          found_at: new Date().toISOString()
        }));
        
        // Merge with existing results and sort by date
        setResults(prev => {
          const combined = [...twitterResults, ...prev];
          return combined
            .sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime())
            .slice(0, 50); // Keep only latest 50
        });
      } else {
        console.log('⚠️ No Twitter data returned');
        toast.info('No recent tweets found from your connected accounts.');
      }
      
    } catch (error) {
      console.error('❌ Error loading Twitter feeds:', error);
      toast.error('Failed to load Twitter feeds. Please check your connection and try again.');
    }
  };

  const loadQueries = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('social_listening_queries')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQueries(data || []);
    } catch (error) {
      console.error('Error loading queries:', error);
      toast.error('Failed to load listening queries');
    }
  };

  const loadResults = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('social_listening_results')
        .select('*')
        .eq('user_id', user.id)
        .order('found_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setResults(data || []);
    } catch (error) {
      console.error('Error loading results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const createQuery = async () => {
    if (!user || !newQuery.trim()) return;

    try {
      const keywords = newQuery.split(',').map(k => k.trim()).filter(k => k);
      
      const { error } = await supabase
        .from('social_listening_queries')
        .insert({
          user_id: user.id,
          name: `Monitor: ${keywords.join(', ')}`,
          keywords,
          platforms: connectedPlatforms, // Use connected platforms instead of hardcoded
          is_active: true
        });

      if (error) throw error;
      
      toast.success('Listening query created!');
      setNewQuery('');
      setShowAddQuery(false);
      loadQueries();
    } catch (error) {
      console.error('Error creating query:', error);
      toast.error('Failed to create query');
    }
  };

  const triggerScan = async () => {
    if (!user) return;

    console.log('🚀 Triggering social listening scan...');
    setScanning(true);
    try {
      console.log('📡 Invoking social-listening-scanner function...');
      const { data, error } = await supabase.functions.invoke('social-listening-scanner');
      
      console.log('📊 Function response:', { data, error });
      
      if (error) {
        console.error('❌ Function error:', error);
        throw error;
      }
      
      console.log('✅ Scan triggered successfully:', data);
      toast.success('Social listening scan triggered!');
      
      // Reload results after a short delay
      setTimeout(() => {
        console.log('🔄 Reloading results and queries...');
        loadResults();
        loadQueries();
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error triggering scan:', error);
      toast.error(`Failed to trigger scan: ${error.message || 'Unknown error'}`);
    } finally {
      setScanning(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'reddit': return '🤖';
      case 'hackernews': return '🟠';
      case 'twitter': return '𝕏';
      case 'news': return '📰';
      default: return '🌐';
    }
  };

  const getSentimentColor = (score?: number) => {
    if (!score) return 'bg-gray-100 text-gray-800';
    if (score > 0.6) return 'bg-green-100 text-green-800';
    if (score < 0.4) return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const getSentimentText = (score?: number) => {
    if (!score) return 'Neutral';
    if (score > 0.6) return 'Positive';
    if (score < 0.4) return 'Negative';
    return 'Mixed';
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  useEffect(() => {
    if (user) {
      loadQueries();
      loadResults();
      // Automatically load from all connected platforms
      loadConnectedPlatformFeeds();
    }
  }, [user, connectedPlatforms]);

  const loadConnectedPlatformFeeds = async () => {
    // Automatically load feeds from all connected platforms
    if (connectedPlatforms.includes('twitter')) {
      await loadTwitterFeeds();
    }
    // Future: Add other platform feed loading here
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p>Please log in to access social listening</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full max-w-none space-y-4 sm:space-y-6 overflow-hidden">
      {/* Header with controls */}
      <Card>
        <CardHeader className="pb-3 sm:pb-4">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Search className="h-4 w-4 sm:h-5 sm:w-5" />
                Social Listening Feed
              </CardTitle>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Real-time monitoring of your connected platforms, Reddit communities, Hacker News, and 17+ major news outlets
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">{"\n"}
              <Button
                onClick={() => setShowAddQuery(!showAddQuery)}
                variant="outline"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Query
              </Button>
              <Button
                onClick={async () => {
                  setScanning(true);
                  try {
                    await triggerScan();
                    await loadConnectedPlatformFeeds();
                  } finally {
                    setScanning(false);
                  }
                }}
                disabled={scanning}
                size="sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${scanning ? 'animate-spin' : ''}`} />
                Scan Now
              </Button>
            </div>
          </div>

          {showAddQuery && (
            <div className="flex flex-col sm:flex-row gap-2 mt-4 w-full">
              <Input
                className="flex-1 min-w-0"
                placeholder="Enter keywords separated by commas (e.g., AI, machine learning, startup)"
                value={newQuery}
                onChange={(e) => setNewQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && createQuery()}
              />
              <Button onClick={createQuery} disabled={!newQuery.trim()} className="whitespace-nowrap">
                Create
              </Button>
            </div>
          )}
        </CardHeader>

        {queries.length > 0 && (
          <CardContent>
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Active Queries:</h4>
              <div className="flex flex-wrap gap-2 max-w-full overflow-hidden">{"\n"}
                {queries.map((query) => (
                  <Badge key={query.id} variant="outline" className="flex items-center gap-1 max-w-full truncate">{"\n"}
                    <Eye className="h-3 w-3" />
                    {query.keywords.join(', ')}
                    {query.last_scanned_at && (
                      <span className="text-xs opacity-70">
                        • {formatTimeAgo(query.last_scanned_at)}
                      </span>
                    )}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Information about monitored sources */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Monitored Sources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm sm:text-base font-medium mb-2 flex items-center gap-2">
                📰 News Outlets (17+ Major Sources)
              </h4>
              <p className="text-sm sm:text-base text-muted-foreground mb-3">
                When you create a query, it automatically scans these news sources:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <p className="font-medium text-muted-foreground mb-2 text-sm">International News:</p>
                  <ul className="space-y-1 text-sm">
                    <li>• BBC News</li>
                    <li>• CNN</li>
                    <li>• Reuters</li>
                    <li>• AP News</li>
                    <li>• NPR</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground mb-2 text-sm">Technology:</p>
                  <ul className="space-y-1 text-sm">
                    <li>• TechCrunch</li>
                    <li>• Wired</li>
                    <li>• Ars Technica</li>
                    <li>• The Verge</li>
                    <li>• Engadget</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground mb-2 text-sm">Business:</p>
                  <ul className="space-y-1 text-sm">
                    <li>• Wall Street Journal</li>
                    <li>• Financial Times</li>
                    <li>• Bloomberg</li>
                    <li>• Forbes</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground mb-2 text-sm">Science & Tech:</p>
                  <ul className="space-y-1 text-sm">
                    <li>• Scientific American</li>
                    <li>• Science Daily</li>
                    <li>• MIT Tech Review</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h4 className="text-sm sm:text-base font-medium mb-2 flex items-center gap-2">
                  🤖 Reddit Communities
                </h4>
                <p className="text-sm text-muted-foreground">
                  Scans relevant subreddits based on your keywords, including r/technology, r/programming, r/artificial, and many more.
                </p>
              </div>
              
              <div>
                <h4 className="text-sm sm:text-base font-medium mb-2 flex items-center gap-2">
                  🟠 Hacker News
                </h4>
                <p className="text-sm text-muted-foreground">
                  Monitors the front page and popular discussions from the tech community.
                </p>
              </div>
              
              {connectedPlatforms.includes('twitter') && (
                <div>
                  <h4 className="text-sm sm:text-base font-medium mb-2 flex items-center gap-2">
                    𝕏 Your Connected Accounts
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Monitors content from your connected Twitter/X accounts and searches for keywords.
                  </p>
                </div>
              )}
            </div>
            
            <div className="bg-muted/30 p-3 sm:p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>How it works:</strong> When you create a query with keywords, the system automatically scans all these sources for mentions. 
                Simply add keywords like "AI", "your brand name", or "competitor name" and click "Scan Now" to start monitoring.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Latest Mentions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p>Loading social mentions...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-8">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No mentions found</h3>
                <p className="text-muted-foreground mb-4">
                  {queries.length === 0 
                    ? connectedPlatforms.includes('twitter') 
                      ? "Add keywords to monitor or click 'Load Twitter Feeds' to see content from your connected accounts."
                      : "Add some keywords to start monitoring social media."
                    : "We haven't found any mentions yet. Try triggering a scan or wait for the next automatic scan."
                  }
              </p>
              {queries.length === 0 && (
                <Button onClick={() => setShowAddQuery(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Query
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4 max-w-full">
              {results.map((result) => (
                <div key={result.id} className="border rounded-lg p-4 hover:bg-muted/30 transition-colors w-full max-w-none overflow-hidden">{"\n"}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                    <div className="flex flex-wrap items-center gap-2">{"\n"}
                      <span className="text-lg">{getPlatformIcon(result.platform)}</span>
                      <Badge variant="outline" className="text-xs">
                        {result.platform}
                      </Badge>
                      <Badge 
                        className={`text-xs ${getSentimentColor(result.sentiment_score)}`}
                      >
                        {getSentimentText(result.sentiment_score)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground whitespace-nowrap">{"\n"}
                      <Clock className="h-3 w-3" />
                      {formatTimeAgo(result.published_at)}
                    </div>
                  </div>
                  
                  <p className="text-sm mb-3 line-clamp-3 break-words overflow-hidden">{"\n"}
                    {result.content_text}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">{"\n"}
                      {result.author_name && (
                        <span>by {result.author_name}</span>
                      )}
                      {result.engagement_metrics && (
                        <div className="flex items-center gap-2">
                          {result.engagement_metrics.score && (
                            <div className="flex items-center gap-1">
                              <ThumbsUp className="h-3 w-3" />
                              {result.engagement_metrics.score}
                            </div>
                          )}
                          {result.engagement_metrics.likes && (
                            <div className="flex items-center gap-1">
                              <ThumbsUp className="h-3 w-3" />
                              {result.engagement_metrics.likes}
                            </div>
                          )}
                          {result.engagement_metrics.retweets && (
                            <div className="flex items-center gap-1">
                              <RefreshCw className="h-3 w-3" />
                              {result.engagement_metrics.retweets}
                            </div>
                          )}
                          {result.engagement_metrics.num_comments && (
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {result.engagement_metrics.num_comments}
                            </div>
                          )}
                          {result.engagement_metrics.replies && (
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {result.engagement_metrics.replies}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(result.content_url, '_blank')}
                      className="text-xs"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RealSocialFeed;